import type { Vec3 } from './vector';

// ---------------------------------------------------------------------------
// Collision system – framework-agnostic interfaces
//
// CollisionWorld 接口定义碰撞检测能力，由各 adapter 包提供具体实现。
// 业务层仅通过此接口交互，不直接依赖 three / three-mesh-bvh 等渲染库。
// ---------------------------------------------------------------------------

export interface CollisionHit {
  point: Vec3;
  normal: Vec3;
  distance: number;
}

/**
 * 碰撞世界抽象。
 *
 * - move: 受碰撞约束的移动（胶囊体 slide 碰撞）
 * - snapToGround: 向下射线吸附到地面
 * - raycast: 通用射线投射
 * - dispose: 释放 BVH / geometry 等加速结构资源
 */
export interface CollisionWorld {
  /** 碰撞约束移动：从 from 到 to，返回碰撞后实际到达位置。 */
  move(from: Vec3, to: Vec3): Vec3;
  /** 向下射线检测地面，返回吸附后位置（脚底 + eyeHeight）。 */
  snapToGround(position: Vec3, eyeHeight: number): Vec3;
  /** 通用射线投射 */
  raycast?(origin: Vec3, direction: Vec3, maxDistance?: number): CollisionHit | null;
  /** 释放 BVH / geometry 等资源 */
  dispose?(): void;
}

// ---------------------------------------------------------------------------
// Walk mode 物理参数配置
// ---------------------------------------------------------------------------

export interface WalkPhysicsConfig {
  /** 移动速度 m/s，默认 2.2 */
  walkSpeed?: number;
  /** 冲刺速度 m/s，默认 4.5 */
  sprintSpeed?: number;
  /** 跳跃初速度 m/s，默认 5.0 */
  jumpSpeed?: number;
  /** 重力加速度 m/s²（正值向下），默认 15 */
  gravity?: number;
  /** 人物眼高 m，默认 1.6 */
  eyeHeight?: number;
  /** 碰撞胶囊半径 m，默认 0.3 */
  capsuleRadius?: number;
  /** 最大可攀爬台阶高度 m，默认 0.3 */
  maxStepHeight?: number;
}

export const DEFAULT_WALK_PHYSICS: Required<WalkPhysicsConfig> = {
  walkSpeed: 2.2,
  sprintSpeed: 4.5,
  jumpSpeed: 5.0,
  gravity: 15,
  eyeHeight: 1.6,
  capsuleRadius: 0.3,
  maxStepHeight: 0.3,
};

// ---------------------------------------------------------------------------
// 碰撞加载相关
// ---------------------------------------------------------------------------

/** 碰撞模型加载结果（框架无关的元数据 + 不透明 native handle）。 */
export interface CollisionMeshResult {
  /** native 场景对象（THREE.Group / Babylon.Mesh 等），调用方不解包。 */
  native: unknown;
  /** 碰撞世界实例，已构建 BVH 加速结构。 */
  world: CollisionWorld;
  /** mesh 数量 */
  meshCount: number;
}

/**
 * 源模型坐标系约定。
 * - 'three' / 'opengl': 右手 Y-up，Z 朝向观察者（Three.js / OpenGL）
 * - 'babylon': 左手 Y-up，Z 朝向屏幕内（Babylon.js）
 */
export type CoordinateSystem = 'three' | 'babylon';

/** 碰撞模型加载选项 */
export interface CollisionLoadOptions {
  url: string;
  /** 是否生成 debug wireframe 材质 */
  debug?: boolean;
  /** 碰撞胶囊半径 m，传入则覆盖 MeshCollisionWorld 默认值 */
  capsuleRadius?: number;
  /** 最大可攀爬台阶高度 m */
  maxStepHeight?: number;
  /** 源 GLB 的坐标系，默认 'babylon'（我们的产物默认是 Babylon 坐标系） */
  coordinateSystem?: CoordinateSystem;
}
